import { useEffect, useState } from 'react';
import { Switch, TextField } from '@mui/material';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { CustomFieldDefinition } from '../../../types';
import {
  fetchCustomFieldDefinitionsApi,
  createCustomFieldDefinitionApi,
  updateCustomFieldDefinitionApi
} from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { KNOWN_CUSTOM_FIELDS } from './knownCustomFields';
import { DataTypeEditor } from './DataTypeEditor';
import { DATA_TYPE_LIST, DataTypeValue, normaliseFieldType } from '../../../utils/customFieldTypes';
import { useChangeFieldTypeWithConfirm } from './useChangeFieldTypeWithConfirm';
import { saveFieldTypeOrChoices } from './saveFieldTypeOrChoices';
import { getUser, isCapabilityDenied } from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';

export interface CustomFieldsFormProps {
  accountId: string;
  parentAccountId?: string;
  onSave?: () => void | Promise<void>;
}

type FieldRow = {
  systemKey: string;
  systemLabel: string;
  definitionId: string | null;
  name: string;
  originalName: string;
  fieldType: DataTypeValue;
  choices: string[];
  originalFieldType: DataTypeValue;
  originalChoicesKey: string;
  enabled: boolean;
  originalEnabled: boolean;
  isEditable: boolean;
  originalIsEditable: boolean;
  updatedAt: string | null;
  modifiedByName: string | null;
  dirty: boolean;
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const matchDefinition = (defs: CustomFieldDefinition[], systemKey: string) =>
  defs.find((d) => d.options?.system_key === systemKey);

const choicesKey = (choices: string[]) => JSON.stringify(choices);

const buildRows = (defs: CustomFieldDefinition[]): FieldRow[] =>
  KNOWN_CUSTOM_FIELDS.map((f) => {
    const def = matchDefinition(defs, f.systemKey);
    const fieldType = normaliseFieldType(def?.field_type ?? f.fieldType);
    const choices = def?.options?.choices ?? [];
    return {
      systemKey: f.systemKey,
      systemLabel: f.systemLabel,
      definitionId: def?.id ?? null,
      name: def?.name ?? f.defaultName,
      originalName: def?.name ?? f.defaultName,
      fieldType,
      choices,
      originalFieldType: fieldType,
      originalChoicesKey: choicesKey(choices),
      enabled: def ? !def.is_archived : false,
      originalEnabled: def ? !def.is_archived : false,
      // Inventory fields are editable by default, so an unsaved row offers what the api would create.
      isEditable: def?.is_editable ?? true,
      originalIsEditable: def?.is_editable ?? true,
      updatedAt: def?.updated_at ?? null,
      modifiedByName: def?.modified_by_name ?? null,
      dirty: false
    };
  });

export const CustomFieldsForm = ({ accountId, parentAccountId, onSave }: CustomFieldsFormProps) => {
  const [rows, setRows] = useState<FieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { change: changeFieldTypeWithConfirm, dialog: incompatibleValuesDialog } =
    useChangeFieldTypeWithConfirm('inventory');
  const createDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_CREATE, getUser());
  const updateDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_UPDATE, getUser());
  const changeTypeDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_CHANGE_FIELD_TYPE, getUser());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await fetchCustomFieldDefinitionsApi(accountId, parentAccountId);
      if (apiError) {
        setError(apiError.message);
      } else {
        setRows(buildRows(data ?? []));
      }
      setLoading(false);
    };
    load();
  }, [accountId, parentAccountId]);

  const handleNameChange = (systemKey: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.systemKey === systemKey ? { ...r, name: value, dirty: true } : r)));
  };

  const handleToggle = (systemKey: string) => {
    setRows((prev) => prev.map((r) => (r.systemKey === systemKey ? { ...r, enabled: !r.enabled, dirty: true } : r)));
  };

  const handleEditableToggle = (systemKey: string) => {
    setRows((prev) =>
      prev.map((r) => (r.systemKey === systemKey ? { ...r, isEditable: !r.isEditable, dirty: true } : r))
    );
  };

  const handleFieldTypeChange = (systemKey: string, value: DataTypeValue) => {
    setRows((prev) => prev.map((r) => (r.systemKey === systemKey ? { ...r, fieldType: value, dirty: true } : r)));
  };

  const handleChoicesChange = (systemKey: string, choices: string[]) => {
    setRows((prev) => prev.map((r) => (r.systemKey === systemKey ? { ...r, choices, dirty: true } : r)));
  };

  const applyRowFromDefinition = (systemKey: string, def: CustomFieldDefinition) => {
    setRows((prev) =>
      prev.map((r) =>
        r.systemKey === systemKey
          ? {
              ...r,
              definitionId: def.id,
              originalName: def.name,
              originalEnabled: !def.is_archived,
              fieldType: normaliseFieldType(def.field_type),
              choices: def.options?.choices ?? [],
              originalFieldType: normaliseFieldType(def.field_type),
              originalChoicesKey: choicesKey(def.options?.choices ?? []),
              isEditable: def.is_editable,
              originalIsEditable: def.is_editable,
              updatedAt: def.updated_at,
              modifiedByName: def.modified_by_name ?? null,
              dirty: false
            }
          : r
      )
    );
  };

  const fail = (message: string) => {
    setError(message);
    setSaving(false);
    return false;
  };

  const saveTypeOrChoices = async (row: FieldRow, definitionId: string): Promise<boolean> => {
    const result = await saveFieldTypeOrChoices(
      definitionId,
      row,
      choicesKey(row.choices) !== row.originalChoicesKey,
      changeFieldTypeWithConfirm
    );
    if (!result) return true;
    if (result.cancelled) {
      setSaving(false);
      return false;
    }
    if (result.error) return fail(result.error.message);
    if (result.data) applyRowFromDefinition(row.systemKey, result.data);
    return true;
  };

  // A row nobody changed stays out of the save: a set may refuse the row's edit while still allowing its type change.
  const saveRowDetails = async (row: FieldRow, definitionId: string): Promise<boolean> => {
    const editabilityChanged = row.isEditable !== row.originalIsEditable;
    if (row.name === row.originalName && row.enabled === row.originalEnabled && !editabilityChanged) return true;
    const { data, error: apiError } = await updateCustomFieldDefinitionApi(definitionId, {
      name: row.name,
      is_archived: !row.enabled,
      ...(editabilityChanged ? { is_editable: row.isEditable } : {})
    });
    if (apiError) return fail(apiError.message);
    if (data) applyRowFromDefinition(row.systemKey, data);
    return true;
  };

  const createRow = async (row: FieldRow): Promise<boolean> => {
    const known = KNOWN_CUSTOM_FIELDS.find((f) => f.systemKey === row.systemKey)!;
    const options =
      row.fieldType === DATA_TYPE_LIST
        ? { system_key: row.systemKey, choices: row.choices }
        : { system_key: row.systemKey };
    const { data, error: apiError } = await createCustomFieldDefinitionApi(accountId, {
      name: row.name,
      entity_type: known.entityType,
      field_type: row.fieldType,
      is_editable: row.isEditable,
      options
    });
    if (apiError) return fail(apiError.message);
    if (data) applyRowFromDefinition(row.systemKey, data);
    return true;
  };

  const saveRow = async (row: FieldRow): Promise<boolean> => {
    if (row.definitionId) {
      return (await saveTypeOrChoices(row, row.definitionId)) && saveRowDetails(row, row.definitionId);
    }
    return row.enabled ? createRow(row) : true;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    for (const row of rows.filter((r) => r.dirty)) {
      if (!(await saveRow(row))) return;
    }
    setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
    await onSave?.();
    setSaving(false);
  };

  if (loading) return <ComponentSpinner label="Loading..." />;

  return (
    <div>
      <TextBlock
        className="mb-1"
        color="grey10"
        size="lg">
        Custom Fields
      </TextBlock>
      <TextSpan
        className="block mb-4"
        color="grey5"
        size="xs">
        Configure custom fields and their column labels for users of this Account.
      </TextSpan>

      {error && (
        <TextSpan
          className="block mb-3 text-red-500"
          color="grey10"
          size="sm">
          {error}
        </TextSpan>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2">
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-1/6">Custom Field</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-1/5">Custom Name</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-1/4">Data Type</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-1/6">Modified by</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-1/6">Modified Date</th>
            <th className="text-center py-2 pr-4 font-medium text-gray-700 w-1/12">Display in Table</th>
            <th className="text-center py-2 font-medium text-gray-700 w-1/12">Editable</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const disabled = !row.definitionId && !row.enabled;
            const writeDenied = row.definitionId ? updateDenied : createDenied;
            const typeDenied = row.definitionId ? changeTypeDenied : createDenied;
            return (
              <tr
                key={row.systemKey}
                className="border-b align-top">
                <td className="py-3 pr-4">
                  <TextSpan
                    color="grey10"
                    size="sm">
                    {row.systemLabel}
                  </TextSpan>
                </td>
                <td className="py-3 pr-4">
                  <TextField
                    size="small"
                    value={row.name}
                    disabled={disabled || writeDenied}
                    onChange={(e) => handleNameChange(row.systemKey, e.target.value)}
                    inputProps={{ 'aria-label': `Custom name for ${row.systemLabel}` }}
                    sx={{ width: '100%' }}
                  />
                </td>
                <td className="py-3 pr-4">
                  <DataTypeEditor
                    fieldType={row.fieldType}
                    choices={row.choices}
                    onFieldTypeChange={(value) => handleFieldTypeChange(row.systemKey, value)}
                    onChoicesChange={(choices) => handleChoicesChange(row.systemKey, choices)}
                    readOnly={disabled || typeDenied}
                    choicesReadOnly={
                      disabled || (!!row.definitionId && updateDenied && row.fieldType === row.originalFieldType)
                    }
                    ariaLabel={`Data type for ${row.systemLabel}`}
                  />
                </td>
                <td className="py-3 pr-4">
                  <TextSpan
                    color="grey5"
                    size="sm">
                    {row.modifiedByName ?? '—'}
                  </TextSpan>
                </td>
                <td className="py-3 pr-4">
                  <TextSpan
                    color="grey5"
                    size="sm">
                    {formatDate(row.updatedAt)}
                  </TextSpan>
                </td>
                <td className="py-3 pr-4 text-center">
                  <Switch
                    size="small"
                    checked={row.enabled}
                    disabled={writeDenied}
                    onChange={() => handleToggle(row.systemKey)}
                    sx={{
                      '.Mui-checked': { color: '#3B6FE8 !important' },
                      '.MuiSwitch-track': { backgroundColor: '#878EBE !important' }
                    }}
                    inputProps={{ 'aria-label': `Display ${row.systemLabel} in table` }}
                  />
                </td>
                <td className="py-3 text-center">
                  <Switch
                    size="small"
                    checked={row.isEditable}
                    disabled={disabled || writeDenied}
                    onChange={() => handleEditableToggle(row.systemKey)}
                    sx={{
                      '.Mui-checked': { color: '#3B6FE8 !important' },
                      '.MuiSwitch-track': { backgroundColor: '#878EBE !important' }
                    }}
                    inputProps={{ 'aria-label': `Allow client users to edit ${row.systemLabel}` }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-end mt-4">
        {saving && <ComponentSpinner label="Saving..." />}
        {!saving && (
          <button
            className="px-4 py-2 bg-indigo text-white rounded text-sm hover:bg-indigo-mid disabled:opacity-50"
            disabled={!rows.some((r) => r.dirty)}
            onClick={handleSave}>
            Save
          </button>
        )}
      </div>
      {incompatibleValuesDialog}
    </div>
  );
};
